import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/* ===== TERMS GATE: block checkout in cart drawer until checkbox is ticked ===== */
(() => {
  const STORAGE_KEY = "terms_gate_agreed_v1";
  const DRAWER_SEL = "cart-drawer-component";
  const GATE_SEL = "[data-terms-gate]";
  const CB_SEL = "[data-terms-checkbox]";
  const ERR_SEL = "[data-terms-error]";

  const CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    'button[name="checkout"]',
    'button[type="submit"][name="checkout"]',
    'button[formaction="/checkout"]',
    'button[formaction^="/checkout"]',
    'a[href="/checkout"]',
    'a[href^="/checkout"]',
    '[on\\:click*="checkout"]'
  ].join(",");

  const storeGet = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };
  const storeSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch {} };

  const qDrawer = () => document.querySelector(DRAWER_SEL);

  const getGate = (drawer) => {
    const gate = drawer?.querySelector(GATE_SEL);
    if (!gate) return null;
    const checkbox = gate.querySelector(CB_SEL);
    const err = gate.querySelector(ERR_SEL);
    if (!checkbox || !err) return null;
    return { gate, checkbox, err };
  };

  const checkoutTargets = (drawer) => Array.from(drawer.querySelectorAll(CHECKOUT_SELECTOR));

  // "Blocked" – vizualiai pilkesnis + logiškai blokuojam click/submit per preventDefault
  const paintBlocked = (el, blocked) => {
    el.style.opacity = blocked ? "0.55" : "1";
    el.setAttribute("aria-disabled", blocked ? "true" : "false");
  };

  const apply = () => {
    const drawer = qDrawer();
    if (!drawer) return;

    const gateState = getGate(drawer);
    if (!gateState) return;

    const { checkbox, err } = gateState;

    if (storeGet(STORAGE_KEY) === "true") checkbox.checked = true;

    const ok = !!checkbox.checked;
    storeSet(STORAGE_KEY, ok ? "true" : "false");

    checkoutTargets(drawer).forEach((t) => paintBlocked(t, !ok));
    if (ok) err.style.display = "none";
    // jei ne ok – error nerodom kol žmogus bando spaust checkout
  };

  const bindOnce = () => {
    const drawer = qDrawer();
    if (!drawer || drawer.__termsGateBound) return;

    drawer.__termsGateBound = true;

    // Checkbox change
    drawer.addEventListener("change", (e) => {
      if (e.target && e.target.matches && e.target.matches(CB_SEL)) apply();
    }, true);

    // Block checkout click
    drawer.addEventListener("click", (e) => {
      const d = qDrawer();
      if (!d) return;

      const gateState = getGate(d);
      if (!gateState) return;

      const { checkbox, err } = gateState;

      const target = e.target?.closest?.(CHECKOUT_SELECTOR);
      if (!target) return;

      if (!checkbox.checked) {
        e.preventDefault();
        e.stopPropagation();
        err.style.display = "block";
        checkbox.focus?.();
      }
    }, true);

    // Block any form submit that leads to checkout
    drawer.addEventListener("submit", (e) => {
      const d = qDrawer();
      if (!d) return;

      const gateState = getGate(d);
      if (!gateState) return;

      const { checkbox, err } = gateState;
      if (!checkbox.checked) {
        e.preventDefault();
        e.stopPropagation();
        err.style.display = "block";
        checkbox.focus?.();
      }
    }, true);

    // Re-apply on re-render (qty change, remove, etc.)
    const mo = new MutationObserver(() => apply());
    mo.observe(drawer, { childList: true, subtree: true });
    drawer.__termsGateObserver = mo;
  };

  const start = () => {
    bindOnce();
    apply();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
